import {
  AllocatableLoadSettingsText,
  ContainerAction,
  ContainerStatusDataGrid,
  ContainerStatusFilterFormModal,
  ContainerStatusFilterFormValues, ContainerStatusQuickFilter,
  GridSort,
  isDefaultFilter,
  ResizeableContent, TagDefinitionMultiSelect,
  ThemedPage,
  Tooltip,
  useApp,
  useContainerAction,
  useDownloadContainerStatusReport
} from "@ots-frontend/ots-ui";
import {
  ActionIcon,
  Alert,
  Card,
  Container,
  Divider,
  Group,
  Loader,
  LoadingOverlay,
  Menu,
  rem,
  Stack,
  Text,
  Title,
  UnstyledButton
} from "@mantine/core";
import {
  IconAdjustments,
  IconArrowsMaximize,
  IconBellExclamation,
  IconChevronDown,
  IconFilter,
  IconSearch, IconTag
} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {getAppCategory} from "../../store/ui/selectors";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
  adjustToTimeZone,
  allocatableLoadSettingsListSelector,
  cargoSubtype,
  containerStatusEntitiesSelector,
  ContainerStatusEntity,
  firstOrUndefined,
  getObjectId, TagDefinitionEntity,
  transportCategory,
  useSearchAllocatableLoadSettingsQuery, useTagDefinitionSearchQuery
} from "@ots-frontend/ots-data";
import {useDisclosure, useFullscreen} from "@mantine/hooks";
import {baseQueryApi} from "../../store/api";
import {useDispatch} from "react-redux";
import {skipToken} from "@reduxjs/toolkit/query";
import {CompactSelection, DataEditorRef, GridSelection, Item} from "@glideapps/glide-data-grid";
import {useGetContainerStatusInsights} from "../../components/hooks/useGetContainerStatusInsights";
import {
  getContainerStatusShutInPageFilter,
  getContainerStatusShutInPageState
} from "../../store/container-status-shut-in-page/selectors";
import {CellClickedEventArgs} from "@glideapps/glide-data-grid/dist/ts/data-grid/data-grid-types";
import {
  setContainerStatusShutInPageFilter,
  setContainerStatusShutInPageStateCell,
  setContainerStatusShutInPageStateSort,
  setContainerStatusShutInPageStateWidths
} from "../../store/container-status-shut-in-page/actions";
import {setContainerStatusPageFilter} from "../../store/container-status-page/actions";

export type ContainerStatusPageProps = {
  // props here
}

export const ContainerStatusShutInPage = (props: ContainerStatusPageProps) => {
  const {} = props

  const appCategory = useAppSelector(getAppCategory);
  const filter = useAppSelector(getContainerStatusShutInPageFilter)
  const gridState = useAppSelector(getContainerStatusShutInPageState)

  const {ref, toggle} = useFullscreen();
  const dataEditorRef = useRef<DataEditorRef>(null);

  const [scrollTimerReference, setScrollTimerReference] = useState<NodeJS.Timeout | undefined>(undefined);
  const [showSearch, setShowSearch] = useState(false);
  const onSearchClose = useCallback(() => setShowSearch(false), []);
  const [showTagFilter, setShowTagFilter] = useState(false);

  const [selection, setSelection] = useState<GridSelection>({
    columns: CompactSelection.empty(),
    rows: CompactSelection.empty()
  });

  const [colWidths, setColWidths] = useState<(number | undefined)[]>(gridState.widths)
  const [sort, setSort] = useState<GridSort | undefined>(gridState.sort)

  const [filterOpened, filterHandler] = useDisclosure(false);
  const {t} = useTranslation(["translation", "common"]);
  const dispatch = useDispatch()
  const {regionId} = useApp()

  useEffect(() => {
    setScrollTimerReference(
      setTimeout(() => {
        if (gridState?.cell) {
          dataEditorRef?.current?.scrollTo(0, gridState.cell[1], "both", 0, 0, {
            vAlign: "start",
            hAlign: "start"
          });
          setSelection(prevState => gridState.cell ? ({
            ...prevState,
            current: {
              cell: [0, gridState.cell[1]],
              range: {
                x: 0,
                y: gridState.cell[1],
                width: 1,
                height: 1
              },
              rangeStack: []
            }
          }) : ({...prevState}));
        }
      }, 200)
    );

    return () => {
      clearTimeout(scrollTimerReference)
    }
  }, []);

  const selectContainerStatuses = useMemo(() => {
    return containerStatusEntitiesSelector();
  }, []);

  const {
    groupBy: {
      potentialShutIns
    },
    isLoading,
    isFetching,
    refetch
  } = useGetContainerStatusInsights()

  const {
    objectIds: taggedObjectIds,
  } = useTagDefinitionSearchQuery({
    tagDefinitions: filter.tagDefinitions,
    objectTypes: ["CONTAINER"]
  })

  const containerStatuses = useMemo(() => {
    return selectContainerStatuses({data: potentialShutIns}, {
      containerIds: taggedObjectIds,
      companyId: getObjectId(filter?.company),
      operatingAreaId: getObjectId(filter?.operatingArea),
      routeGroupIds: filter?.routeGroups?.map(getObjectId),
      routeIds: filter?.routes?.map(getObjectId),
      siteId: getObjectId(filter?.site),
      assignedCompanyId: getObjectId(filter?.assignedCompany),
      unassignedOnly: filter?.unassignedOnly,
      transportViaPipeline: filter?.transportViaPipeline,
      transportOilViaPipeline: filter?.transportOilViaPipeline,
      usability: filter?.usability,
      restriction: filter?.restriction,
      showNoData: filter?.showNoData,
      showDoNotUse: filter?.showDoNotUse,
      highNonPrimaryVolumeOnly: filter?.highNonPrimaryVolumeOnly,
      criticalStatusOnly: filter?.criticalStatusOnly,
      availableLoadsOnly: filter?.availableLoadsOnly,
    })
  }, [potentialShutIns, filter, taggedObjectIds, selectContainerStatuses])

  const {
    onMultiContainerActionClick
  } = useContainerAction()

  const {
    downloadContainerStatusReport,
  } = useDownloadContainerStatusReport({baseQueryApi})

  const selectAllocatableLoadSettings = useMemo(() => {
    return allocatableLoadSettingsListSelector();
  }, []);

  const {allocatableLoadSetting} = useSearchAllocatableLoadSettingsQuery(regionId ? {
    categories: [appCategory.code],
    objectIds: [regionId],
    dttm: adjustToTimeZone().getTime(),
  } : skipToken, {
    selectFromResult: (result) => ({
      allocatableLoadSetting: firstOrUndefined(selectAllocatableLoadSettings(result)),
      isLoading: result.isLoading
    })
  });

  const cargoSubtypes = useMemo(() => {
    if (appCategory === transportCategory.water) {
      return new Set([cargoSubtype.water, cargoSubtype.oil])
    } else {
      return new Set([cargoSubtype.oil, cargoSubtype.water])
    }
  }, [appCategory])

  const onActionClick = useCallback((data: ContainerStatusEntity[], action: ContainerAction) => {
    onMultiContainerActionClick(data, action)
  }, [onMultiContainerActionClick])

  const onFilterSubmit = useCallback((filter: ContainerStatusFilterFormValues) => {
    dispatch(setContainerStatusShutInPageFilter(filter))
    filterHandler.close()
  }, [dispatch, filterHandler])

  const onQuickFilter = useCallback((quickFilter: ContainerStatusFilterFormValues) => {
    dispatch(setContainerStatusPageFilter({...filter, ...quickFilter}))
  }, [dispatch, filter])

  const onTagDefinitionChange = useCallback((defs: TagDefinitionEntity[]) => {
    dispatch(setContainerStatusPageFilter({
      ...filter,
      tagDefinitions: defs.map((def) => ({id: def.id, key: def.key})),
    }))
  }, [dispatch, filter])

  const onExportClick = useCallback(() => {
    downloadContainerStatusReport(appCategory)
  }, [downloadContainerStatusReport, appCategory])

  const onCellClicked = useCallback((cell: Item, event: CellClickedEventArgs) => {
    dispatch(setContainerStatusShutInPageStateCell([cell[0], cell[1]]))
  }, [dispatch])

  useEffect(() => {
    return () => {
      dispatch(setContainerStatusShutInPageStateSort(sort))
    }
  }, [sort, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setContainerStatusShutInPageStateWidths(colWidths))
    }
  }, [colWidths, dispatch]);

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Alert icon={<IconBellExclamation size="1rem"/>} color="red" variant="light">
                    {t("common.entity.container.potentialshutin.desc", "Potential Shut-Ins are containers that are set to 'Do Not Dispatch' but whose fill or time to fill is critically high.")}
                  </Alert>
                  <Group position="apart" mt="sm">
                    <Group>
                      <Title order={4}>
                        {t("page.containerlevels.shutin.title", "Potential Shut-In Container Levels", {ns: "translation"})}
                      </Title>
                      {allocatableLoadSetting &&
                        <Tooltip
                          withinPortal={true}
                          label={
                            <AllocatableLoadSettingsText
                              data={allocatableLoadSetting}
                              separator={"\n"}
                              style={{whiteSpace: "pre-wrap"}}/>
                          }
                        >
                          <IconAdjustments size="1.125rem"/>
                        </Tooltip>
                      }
                    </Group>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              Actions
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("actions.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                        <Menu.Item onClick={() => onExportClick()}>
                          {t("actions.export", "Export", {ns: "common"})}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                  <Group p="md">
                    <ActionIcon
                      variant={showSearch ? "filled" : "subtle"}
                      color={showSearch ? "primary" : undefined}
                      onClick={() => setShowSearch(!showSearch)}>
                      <IconSearch size="1.125rem"/>
                    </ActionIcon>
                    <ActionIcon
                      variant={!isDefaultFilter(filter) ? "filled" : "subtle"}
                      color={!isDefaultFilter(filter) ? "service-work" : undefined}
                      onClick={() => filterHandler.open()}>
                      <IconFilter size="1.125rem"/>
                    </ActionIcon>
                    <ActionIcon
                      variant={(showTagFilter || filter.tagDefinitions?.length > 0) ? "filled" : "subtle"}
                      color={(showTagFilter || filter.tagDefinitions?.length > 0) ? "service-work" : undefined}
                      onClick={() => setShowTagFilter(!showTagFilter)}>
                      <IconTag size="1.125rem"/>
                    </ActionIcon>
                    <ActionIcon
                      variant={"subtle"}
                      onClick={() => toggle()}>
                      <IconArrowsMaximize size="1.125rem"/>
                    </ActionIcon>
                    <Divider orientation="vertical"/>
                    {containerStatuses.length > 0 &&
                      <Group>
                        <Text size="sm" c="dimmed">
                          {containerStatuses.length + " " + t("common.entity.container.plural", "Containers")}
                        </Text>
                      </Group>
                    }
                    {isFetching &&
                      <Group spacing="xs">
                        <Loader size="xs" color="gray"/>
                        <Text size="sm" c="dimmed">
                          {t("common.state.refreshing", "Refreshing")}
                        </Text>
                      </Group>
                    }
                  </Group>

                  <Divider variant="dashed"/>

                  <ContainerStatusQuickFilter
                    mt="md"
                    showDispatchFilters={false}
                    value={filter}
                    onChange={onQuickFilter}/>

                  {showTagFilter &&
                    <TagDefinitionMultiSelect
                      size="xs"
                      mt="md"
                      withinPortal={true}
                      onEntityChange={onTagDefinitionChange}
                      value={filter.tagDefinitions?.map((def) => def.id)}
                    />
                  }

                </Card>
                <LoadingOverlay visible={isLoading} overlayBlur={2}/>
                <div
                  ref={ref}
                  style={{height: "100%"}}>
                  <ContainerStatusDataGrid
                    ref={dataEditorRef}
                    columnStorageKey="ots.container-status-shut-in-grid.columns"
                    height="100%"
                    showSearch={showSearch}
                    onSearchClose={onSearchClose}
                    actions={[
                      "TOGGLE_DNU",
                      "TOGGLE_DO_NOT_DISPATCH",
                      "TOGGLE_DISPATCH_IGNORE_MAX_NON_PRIMARY_VOLUME",
                      "DISPATCH_REQUEST",
                      "TOGGLE_LACT_METER_DNU",
                      "TOGGLE_SITE_TRANSPORT_VIA_PIPELINE",
                      "TOGGLE_SITE_TRANSPORT_OIL_VIA_PIPELINE",
                      "TOGGLE_REJECTION",
                      "MANAGE_TAGS",
                    ]}
                    onActionClick={onActionClick}
                    onCellClicked={onCellClicked}
                    gridSelection={selection}
                    onGridSelectionChange={setSelection}
                    colWidths={gridState.widths}
                    onColumnResize={setColWidths}
                    sort={gridState.sort}
                    onSort={setSort}
                    cargoSubtypes={cargoSubtypes}
                    data={containerStatuses}
                    colOptions={{
                      showSiteVolumeCol: true,
                    }}
                  />
                </div>
              </Stack>

              <ContainerStatusFilterFormModal
                title={t<string>("common.action.filter", "Filter", {ns: "common"})}
                opened={filterOpened}
                onClose={filterHandler.close}
                onSubmit={onFilterSubmit}
                defaultValues={{
                  ...filter
                }}
              />

            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
